import {
  Asset,
  AssetDataObject,
  Content,
  ContentAsset,
  ContentAssetDataObject,
  ContentDataObject,
  ContextTypeEnum,
  DataObject,
  GeneralStatusEnum, Group, GroupDataObject,
  Item,
  ItemDataObject,
  ItemGroup,
  ItemGroupDataObject,
  ItemSetting,
  ItemSettingDataObject,
  ItemTypeEnum,
  ItemUser,
  ItemUserDataObject,
  LessonPlan,
  LessonPlanDataObject,
  Message,
  MessageDataObject,
  MessageGroup,
  MessageGroupDataObject,
  UserGroup, UserGroupDataObject
} from 'able-lib'

function buildAssetDataObject (asset: Asset): DataObject<AssetDataObject> {
  if (!asset) return undefined

  return {
    data: {
      id: asset.id || undefined,
      class: asset.class,
      description: asset.description,
      type: asset.type,
      url: asset.url,
      is_shared: asset.isShared
    },
    on_conflict: {
      constraint: 'asset_pkey',
      update_columns: [
        'id', 'class', 'description', 'type', 'url', 'is_shared'
      ]
    }
  }
}

function buildContentAssetDataObject (contentAssets: Array<ContentAsset>): DataObject<Array<ContentAssetDataObject>> {
  const contentAssetDataObject: Array<ContentAssetDataObject> = []

  if (!contentAssets) return undefined

  contentAssets.forEach((contentAsset: ContentAsset) => {
    contentAssetDataObject.push({
      asset: buildAssetDataObject(contentAsset.asset)
    })
  })

  if (contentAssetDataObject.length === 0) return undefined

  return {
    data: contentAssetDataObject,
    on_conflict: {
      constraint: 'content_asset_pkey',
      update_columns: [
        'asset_id', 'content_id'
      ]
    }
  }
}

export function buildContentDataObject (content: Content): DataObject<ContentDataObject> {
  if (!content) return undefined

  return {
    data: {
      id: content.id || undefined,
      title: content.title,
      body: content.body,
      annotation: content.annotation,
      content_assets: buildContentAssetDataObject(content.contentAssets)
    },
    on_conflict: {
      constraint: 'content_pkey',
      update_columns: [
        'id', 'title', 'body', 'annotation'
      ]
    }
  }
}

function buildItemUsersDataObject (itemUsers: Array<ItemUser>): DataObject<Array<ItemUserDataObject>> {
  const itemUsersDataObject: Array<ItemUserDataObject> = []

  if (!itemUsers) return undefined

  itemUsers.forEach((itemUser: ItemUser) => {
    itemUsersDataObject.push({
      user_id: itemUser.user.id,
      role_id: itemUser.role.id,
      data: itemUser.data
    })
  })

  if (itemUsersDataObject.length === 0) return undefined

  return {
    data: itemUsersDataObject,
    on_conflict: {
      constraint: 'item_user_pkey',
      update_columns: [
        'user_id', 'data'
      ]
    }
  }
}

function buildItemGroupsDataObject (itemGroups: Array<ItemGroup>): DataObject<Array<ItemGroupDataObject>> {
  const itemGroupsDataObject: Array<ItemGroupDataObject> = []

  if (!itemGroups) return undefined

  itemGroups.forEach((itemGroup: ItemGroup) => {
    itemGroupsDataObject.push({
      group_id: itemGroup.group.id,
      data: itemGroup.data
    })
  })

  if (itemGroupsDataObject.length === 0) return undefined

  return {
    data: itemGroupsDataObject,
    on_conflict: {
      constraint: 'item_group_pkey',
      update_columns: [
        'group_id', 'data'
      ]
    }
  }
}

function buildItemSettingsDataObject (itemSettings: Array<ItemSetting>): DataObject<Array<ItemSettingDataObject>> {
  const itemSettingsDataObject: Array<ItemSettingDataObject> = []

  if (!itemSettings) return undefined

  itemSettings.forEach((itemSetting: ItemSetting) => {
    itemSettingsDataObject.push({
      key: itemSetting.key,
      user_id: itemSetting.user ? itemSetting.user.id : undefined,
      group_id: itemSetting.group ? itemSetting.group.id : undefined,
      value: itemSetting.value
    })
  })

  if (itemSettingsDataObject.length === 0) return undefined

  return {
    data: itemSettingsDataObject,
    on_conflict: {
      constraint: 'item_setting_pkey',
      update_columns: [
        'key', 'user_id', 'group_id', 'value'
      ]
    }
  }
}

function buildItemTemplateLessonDataObject (lessonPlan: LessonPlan): DataObject<LessonPlanDataObject> {
  if (!lessonPlan || !lessonPlan.number) return undefined

  return {
    data: {
      id: lessonPlan.id || undefined,
      number: lessonPlan.number,
      enhancement: lessonPlan.enhancement || '',
      year: lessonPlan.year,
      lesson: lessonPlan.lesson,
      assessment: lessonPlan.assessment || '',
      subject_id: lessonPlan.subject.id,
      level_id: lessonPlan.level.id,
      objective: lessonPlan.objective,
      platform: lessonPlan.platform || '',
      resources: lessonPlan.resources || '',
      status: lessonPlan.status || ''
    },
    on_conflict: {
      constraint: 'item_template_lessonplan_pkey',
      update_columns: [
        'id', 'number', 'enhancement', 'year', 'lesson', 'assessment', 'subject_id', 'level_id', 'objective', 'platform', 'resources', 'status'
      ]
    }
  }
}

function buildItemsDataObject (items: Array<Item>): DataObject<Array<ItemDataObject>> {
  const itemsDataObject: Array<ItemDataObject> = []

  if (!items) return undefined

  items.forEach((item: Item) => {
    itemsDataObject.push({
      id: item.id || undefined,
      type: item.type,
      order: item.order,
      content: buildContentDataObject(item.content),
      original_item_id: item.originalItem ? item.originalItem.id : undefined,
      items: buildItemsDataObject(item.items),
      item_users: buildItemUsersDataObject(item.itemUsers),
      item_groups: buildItemGroupsDataObject(item.itemGroups),
      item_settings: buildItemSettingsDataObject(item.itemSettings),
      item_template_lessonplan: buildItemTemplateLessonDataObject(item.lessonPlan),
      status: GeneralStatusEnum.PUBLISHED
    })
  })

  if (itemsDataObject.length === 0) return undefined

  return {
    data: itemsDataObject,
    on_conflict: {
      constraint: 'item_pkey',
      update_columns: [
        'id', 'type', 'order', 'content_id', 'original_item_id', 'lesson_template_id', 'status'
      ]
    }
  }
}

export function generateAssignmentObj (assignmendId, recipientUserIds = []) {
  const objects = []
  recipientUserIds.map((userId) => {
    objects.push(
      {
        type: ItemTypeEnum.ASSIGNMENT,
        context_type: ContextTypeEnum.ITEM,
        context_id: assignmendId,
        user_id: userId,
        status: GeneralStatusEnum.DRAFT,
        content: {
          data: {
            body: ''
          }
        }
      }
    )
  })
  return objects
}

export function concatAssignmentAnswers (question, answers) {
  const groupedQuestions = []
  question.forEach((assignmentItem) => {
    groupedQuestions.push({ ...assignmentItem, answer: answers.filter(answer => answer.context_id === assignmentItem.id) })
  })

  return groupedQuestions
}

export default function buildItemDataObject (item: Item): DataObject<ItemDataObject> {
  if (!item) return undefined

  const lessonDataObject: ItemDataObject = {
    id: item.id || undefined,
    type: item.type,
    order: item.order,
    content: buildContentDataObject(item.content),
    original_item_id: item.originalItem ? item.originalItem.id : undefined,
    items: buildItemsDataObject(item.items),
    item_users: buildItemUsersDataObject(item.itemUsers),
    item_groups: buildItemGroupsDataObject(item.itemGroups),
    item_settings: buildItemSettingsDataObject(item.itemSettings),
    item_template_lessonplan: buildItemTemplateLessonDataObject(item.lessonPlan),
    status: GeneralStatusEnum.PUBLISHED,
    start_datetime: item.startDateTime ? new Date(item.startDateTime).toISOString() : null,
    end_datetime: item.startDateTime ? new Date(item.endDateTime).toISOString() : null
  }

  return {
    data: lessonDataObject,
    on_conflict: {
      constraint: 'item_pkey',
      update_columns: [
        'id', 'type', 'order', 'content_id', 'event_id', 'original_item_id', 'lesson_template_id', 'status', 'start_datetime', 'end_datetime'
      ]
    }
  }
}

export function buildUserGroupsDataObject (userGroups: Array<UserGroup>): DataObject<Array<UserGroupDataObject>> {
  const userGroupsDataObject: Array<UserGroupDataObject> = []

  if (!userGroups) return undefined

  userGroups.forEach((userGroup: UserGroup) => {
    userGroupsDataObject.push({
      user_id: userGroup.user ? userGroup.user.id : undefined,
      group_id: userGroup.group ? userGroup.group.id : undefined,
      role_id: userGroup.role ? userGroup.role.id : undefined
    })
  })

  if (userGroupsDataObject.length === 0) return undefined

  return {
    data: userGroupsDataObject,
    on_conflict: {
      constraint: 'user_group_pkey',
      update_columns: [
        'group_id', 'role_id', 'user_id'
      ]
    }
  }
}

export function buildGroupDataObject (group: Group): DataObject<GroupDataObject> {
  if (!group) return undefined

  return {
    data: {
      id: group.id || undefined,
      name: group.name,
      type: group.type,
      user_groups: buildUserGroupsDataObject(group.userGroups)
    },
    on_conflict: {
      constraint: 'group_pkey',
      update_columns: [
        'id', 'name'
      ]
    }
  }
}

export function buildMessageGroupDataObject (messageGroups: Array<MessageGroup>): DataObject<Array<MessageGroupDataObject>> {
  const messageGroupsDataObject: Array<MessageGroupDataObject> = []

  if (!messageGroups) return undefined

  messageGroups.forEach((messageGroup) => {
    messageGroupsDataObject.push({
      group: buildGroupDataObject(messageGroup.group),
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      message: buildMessageDataObject(messageGroup.message)
    })
  })

  if (messageGroupsDataObject.length === 0) return undefined

  return {
    data: messageGroupsDataObject,
    on_conflict: {
      constraint: 'message_group_pkey',
      update_columns: [
        'group_id', 'message_id'
      ]
    }
  }
}

export function buildMessageDataObject (message: Message): DataObject<MessageDataObject> {
  if (!message) return undefined

  return {
    data: {
      id: message.id || undefined,
      type: message.type,
      status: message.status,
      recipient_user_id: message.recipientUser.id,
      sender_user_id: message.senderUser.id,
      data: message.data,
      content: buildContentDataObject(message.content),
      message_groups: buildMessageGroupDataObject(message.messageGroups)
    },
    on_conflict: {
      constraint: 'message_pkey',
      update_columns: [
        'id'
      ]
    }
  }
}
